import React, { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import styles from './attention-threshold-input.module.scss';
import { DEFAULT_ATTENTION_DURATION, DEFAULT_ATTENTION_PERCENTAGE } from '../../constants/attention-threshold-settings';

interface IAttentionThresholdInputProperties {
  percentage?: number;
  duration?: number;
  onSubmit?: (percentage: number, duration: number) => void;
}

function AttentionThresholdInput({ onSubmit }: IAttentionThresholdInputProperties) {
  const overlayPanelReference = useRef<OverlayPanel>(null);
  const [percentage, setPercentage] = useState<number>(DEFAULT_ATTENTION_PERCENTAGE);
  const [duration, setDuration] = useState<number>(DEFAULT_ATTENTION_DURATION);

  const handleOpenButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    overlayPanelReference.current?.toggle(event);
  };

  const handleRefreshButtonClick = () => {
    if (onSubmit) {
      onSubmit(percentage, duration);
      overlayPanelReference.current?.hide();
    }
  };

  return (
    <div className={styles.container}>
      <Button
        label="'Needs Attention' Settings"
        icon="pi pi-exclamation-triangle"
        onClick={handleOpenButtonClick}
        className={styles.toggleButton}
      />

      <OverlayPanel ref={overlayPanelReference} className={styles.overlayPanel}>
        <div className={styles.overlayContent}>
          <h3 className={styles.title}>'Needs Attention' Threshold Settings</h3>
          <p className={styles.description}>
            'Needs Attention' logic: if a sales steering has a date range with <strong>high percentage</strong> of days
            relative to the accommodation contract, it's flagged as <strong>'Needs Attention'</strong>. However, if the
            range is longer than the allowed <strong>duration (months)</strong>, only the first part of the range up to
            the allowed duration is considered for this check.
          </p>
          <div className={styles.inputGroup}>
            <label htmlFor="percentage-input" className={styles.label}>
              Percentage:
            </label>
            <input
              id="percentage-input"
              type="number"
              value={percentage}
              onChange={(event) => setPercentage(Number(event.target.value))}
              className={styles.input}
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="duration-input" className={styles.label}>
              Duration (Months):
            </label>
            <input
              id="duration-input"
              type="number"
              value={duration}
              onChange={(event) => setDuration(Number(event.target.value))}
              className={styles.input}
            />
          </div>
          <Button
            label="Refresh"
            icon="pi pi-replay"
            onClick={handleRefreshButtonClick}
            className={styles.toggleButton}
          />
        </div>
      </OverlayPanel>
    </div>
  );
}

export default AttentionThresholdInput;
